// Copied verbatim from original/src/components/EnglishDatePicker.tsx (ADR-08 §2.1); helper import path changed.
// Needed by the quick-operation modal's due-date block (customer mode, D-097). The two button labels (اليوم /
// + شهر واحد) and the Arabic month names are left as in the copy (calendar labels, hard-coded Arabic in the old
// component); to be keyed when the date picker gets its own extraction task.
import React, { useState, useRef, useEffect } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { toEnglishDigits } from '../lib/format.js';

interface EnglishDatePickerProps {
  value: string; // YYYY-MM-DD
  onChange: (dateStr: string) => void;
  required?: boolean;
  className?: string;
}

export const EnglishDatePicker: React.FC<EnglishDatePickerProps> = ({
  value,
  onChange,
  required = false,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Parse current selected date or fallback to today
  const selectedDate = value ? new Date(value) : new Date();
  const validSelectedDate = isNaN(selectedDate.getTime()) ? new Date() : selectedDate;

  // View state for calendar (year & month)
  const [viewYear, setViewYear] = useState(validSelectedDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(validSelectedDate.getMonth()); // 0-11

  // Update view when value changes
  useEffect(() => {
    if (value) {
      const d = new Date(value);
      if (!isNaN(d.getTime())) {
        setViewYear(d.getFullYear());
        setViewMonth(d.getMonth());
      }
    }
  }, [value]);

  // Close popover on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Month names in Arabic with English numbers
  const monthNamesAr = [
    'كانون الثاني (Jan)', 'شباط (Feb)', 'آذار (Mar)', 'نيسان (Apr)',
    'أيار (May)', 'حزيران (Jun)', 'تموز (Jul)', 'آب (Aug)',
    'أيلول (Sep)', 'تشرين الأول (Oct)', 'تشرين الثاني (Nov)', 'كانون الأول (Dec)'
  ];

  const daysOfWeek = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  // Days in month calculation
  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month, 1).getDay(); // 0 = Sun
  };

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(v => v - 1);
    } else {
      setViewMonth(v => v - 1);
    }
  };

  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(v => v + 1);
    } else {
      setViewMonth(v => v + 1);
    }
  };

  const handleSelectDay = (day: number) => {
    const m = String(viewMonth + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    const formatted = `${viewYear}-${m}-${d}`;
    onChange(formatted);
    setIsOpen(false);
  };

  const handleSetToday = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    const formatted = `${y}-${m}-${d}`;
    onChange(formatted);
    setViewYear(y);
    setViewMonth(now.getMonth());
    setIsOpen(false);
  };

  const handleAddMonth = () => {
    const base = value ? new Date(value) : new Date();
    const target = new Date(base.getFullYear(), base.getMonth() + 1, base.getDate());
    const y = target.getFullYear();
    const m = String(target.getMonth() + 1).padStart(2, '0');
    const d = String(target.getDate()).padStart(2, '0');
    const formatted = `${y}-${m}-${d}`;
    onChange(formatted);
    setViewYear(y);
    setViewMonth(target.getMonth());
    setIsOpen(false);
  };

  // Format date display (YYYY-MM-DD in English digits)
  const displayVal = toEnglishDigits(value || '');

  return (
    <div className="relative w-full" ref={popoverRef}>
      <div className="relative flex items-center">
        <input
          type="text"
          dir="ltr"
          lang="en-US"
          required={required}
          value={displayVal}
          readOnly
          onClick={() => setIsOpen(!isOpen)}
          placeholder="YYYY-MM-DD"
          className={`w-full cursor-pointer bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl pl-10 pr-4 py-3 text-sm font-mono font-bold text-gray-900 dark:text-white focus:outline-none focus:border-blue-500 text-left ${className}`}
        />
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="absolute left-3 top-3.5 text-blue-500 hover:text-blue-600 cursor-pointer"
        >
          <CalendarIcon size={18} />
        </button>
      </div>

      {isOpen && (
        <div className="absolute top-full right-0 mt-2 z-50 w-72 bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-3.5 space-y-3">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 pb-2">
            <button
              type="button"
              onClick={prevMonth}
              className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300 cursor-pointer"
            >
              <ChevronLeft size={18} />
            </button>
            <div className="text-center">
              <span className="font-bold text-xs text-gray-900 dark:text-white block">
                {monthNamesAr[viewMonth]}
              </span>
              <span className="font-mono text-xs font-black text-blue-600 dark:text-blue-400 block" dir="ltr">
                {viewYear}
              </span>
            </div>
            <button
              type="button"
              onClick={nextMonth}
              className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300 cursor-pointer"
            >
              <ChevronRight size={18} />
            </button>
          </div>

          {/* Quick Shortcuts */}
          <div className="flex items-center gap-1.5 text-[11px] font-bold">
            <button
              type="button"
              onClick={handleSetToday}
              className="flex-1 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900 transition-colors cursor-pointer text-center"
            >
              اليوم
            </button>
            <button
              type="button"
              onClick={handleAddMonth}
              className="flex-1 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900 transition-colors cursor-pointer text-center"
            >
              + شهر واحد
            </button>
          </div>

          {/* Weekday headers */}
          <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-mono font-bold text-gray-400">
            {daysOfWeek.map((d, i) => (
              <span key={i}>{d}</span>
            ))}
          </div>

          {/* Calendar days grid */}
          <div className="grid grid-cols-7 gap-1 text-center">
            {Array.from({ length: firstDay }).map((_, i) => (
              <span key={`empty-${i}`} />
            ))}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const mStr = String(viewMonth + 1).padStart(2, '0');
              const dStr = String(day).padStart(2, '0');
              const dateStr = `${viewYear}-${mStr}-${dStr}`;
              const isSelected = value === dateStr;

              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => handleSelectDay(day)}
                  className={`py-1.5 rounded-lg text-xs font-mono font-bold transition-all cursor-pointer flex items-center justify-center ${
                    isSelected
                      ? 'bg-blue-600 text-white font-black shadow-sm'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200'
                  }`}
                  dir="ltr"
                >
                  {day}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
